import Logo from "@components/sections/Logo";
import { useAuthContext } from "@hooks/contexts/auth-context";
import { ClientSessionStageEnum } from "@hooks/dtos/IClientSessionDTO";
import { clientIsValidStage } from "@hooks/functions/Client/stage";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { config } from "config";
import React from "react";
import { FaInstagram, FaTiktok, FaWhatsapp, FaYoutube } from "react-icons/fa";
import { Link } from "react-router-dom";

export const Footer: React.FC = () => {
  const { client } = useAuthContext();
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();
  const year = new Date().getFullYear();
  const comingSoonRoutes = ["/privacy-policy", "/terms", "/about-us"];

  function handleFooterLinkClick(
    event: React.MouseEvent<HTMLAnchorElement>,
    to: string,
  ) {
    if (!comingSoonRoutes.includes(to)) return;

    event.preventDefault();
    pushToastify({
      message: "Em breve.",
      type: "info",
      theme: "light",
    });
  }

  const columns = [
    {
      title: "Produto",
      links: [
        { label: "Campanhas Ativas", to: "/campaign/default/list" },
        { label: "Como Funciona", to: "/#como-funciona" },
        {
          label: "Criar Rifa",
          to: config.urls.panel,
          target: "_blank",
        },
        {
          label: "Financeiro",
          to: "/financial",
          role: [
            ClientSessionStageEnum.VERIFIED,
            ClientSessionStageEnum.IDENTIFIED,
          ],
        },
        {
          label: "Premiações",
          to: "/my-rewards",
          role: [
            ClientSessionStageEnum.VERIFIED,
            ClientSessionStageEnum.IDENTIFIED,
          ],
        },
      ],
    },
    {
      title: "Suporte",
      links: [
        // { label: "Central de Ajuda", to: "/help" },
        { label: "Fale Conosco", to: "/contact-us" },
        { label: "Política de Privacidade", to: "/privacy-policy" },
        { label: "Termos de Uso", to: "/terms" },
      ],
    },
    {
      title: "Empresa",
      links: [
        { label: "Sobre Nós", to: "/about-us" },
        // { label: "Blog", to: "/blog" },
        // { label: "Imprensa", to: "/imprensa" },
      ],
    },
  ];

  const socials = [
    { label: "Instagram", icon: <FaInstagram />, href: "#" },
    { label: "TikTok", icon: <FaTiktok />, href: "#" },
    { label: "WhatsApp", icon: <FaWhatsapp />, href: "#" },
    { label: "YouTube", icon: <FaYoutube />, href: "#" },
  ];

  return (
    <footer
      className="pt-5 pb-3 px-3 mt-5"
      style={{
        background: "#0f0f1a",
        color: "rgba(255,255,255,1)",
      }}
    >
      <div
        className="container px-3 px-md-4 px-lg-0"
        style={{ maxWidth: 1240 }}
      >
        {/* TOP */}
        <div className="row g-4 pb-5 border-bottom border-secondary border-opacity-25">
          {/* BRAND */}
          <div className="col-12 col-lg-4">
            <Logo imgSize="xl" txtSize="md" />

            <p
              className="mt-3 mb-4"
              style={{
                fontSize: "0.9rem",
                lineHeight: 1.6,
                maxWidth: 320,
                color: "rgba(255,255,255,0.6)",
              }}
            >
              A plataforma mais segura e transparente para participar de rifas
              online. Sorteios ao vivo, pagamento seguro e ganhadores reais.
            </p>

            {/* SOCIAL */}
            <div className="d-flex gap-2">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  title={s.label}
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 10,
                    background: "rgba(255,255,255,0.06)",
                    transition: "all .2s",
                    textDecoration: "none",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255,193,7,0.25)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* LINKS */}
          {columns.map((col) => (
            <div key={col.title} className="col-6 col-sm-4 col-lg-2">
              <h6
                className="fw-bold"
                style={{
                  fontSize: "1rem",
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.35)",
                  marginBottom: "1rem",
                }}
              >
                {col.title}
              </h6>

              <ul className="list-unstyled d-flex flex-column gap-2 mb-0">
                {col.links.map((link) => {
                  // --- Check if can see the Menu Option --- //
                  const canSeeMenu = clientIsValidStage(
                    client?.stage,
                    link?.role || [],
                  );

                  if (!canSeeMenu) return null;
                  return (
                    <li key={link.label}>
                      <Link
                        target={link?.target}
                        to={link.to}
                        onClick={(event) =>
                          handleFooterLinkClick(event, link.to)
                        }
                        style={{
                          fontSize: "0.85rem",
                          color: "rgba(255,255,255,1)",
                          textDecoration: "none",
                          transition: "color .2s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.color = "#ffc107")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.color = "rgba(255,255,255,1)")
                        }
                      >
                        {link.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {/* TRUST */}
          <div className="col-12 col-sm-4 col-lg-2">
            <h6
              className="fw-bold"
              style={{
                fontSize: "1rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.35)",
                marginBottom: "1rem",
              }}
            >
              Segurança
            </h6>

            <div className="d-flex flex-column gap-2">
              {[
                "🔒 Pagamento Seguro",
                "✅ Site Verificado",
                "⚡ Suporte Ágil",
              ].map((item) => (
                <div key={item} style={{ fontSize: "0.8rem" }}>
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* BOTTOM */}
        <div className="row align-items-center pt-4">
          <div className="col-12 col-md-6">
            <p style={{ fontSize: "0.8rem", margin: 0 }}>
              © {year} {config.appName}. Todos os direitos reservados.
            </p>
          </div>

          <div className="col-12 col-md-6 text-md-end mt-2 mt-md-0">
            <p style={{ fontSize: "1rem", margin: 0 }}>
              Feito por {config.made.company.name} no Brasil 🇧🇷 ·{" "}
              <Link
                to="/terms"
                onClick={(event) => handleFooterLinkClick(event, "/terms")}
              >
                Termos
              </Link>{" "}
              ·{" "}
              <Link
                to="/privacy-policy"
                onClick={(event) =>
                  handleFooterLinkClick(event, "/privacy-policy")
                }
              >
                Privacidade
              </Link>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
